import React from "react";
import PropTypes from "prop-types";
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import NeoButton from "./NeoButton";

/**
 * NeoDropdown
 * Componente de menu suspenso premium do NeoApp.
 * Facilita a criação de menus de contexto, seletores e botões de ação divididos (split).
 * Suporta tanto renderização rápida por array (items) quanto renderização de filhos customizados (children).
 * Adota automaticamente a tipografia Outfit, sombras outonais elevadas, cantos arredondados,
 * e animações de hover e cliques da marca.
 * Estilos isolados em NeoDropdown.css.
 */
const NeoDropdown = ({
    label = "",
    icon = null, // e.g. 'mdi-dots-vertical' para menu de contexto
    iconRight = "mdi-chevron-down", // chevron padrão
    variant = "secondary", // 'primary' | 'chocolate' | 'secondary' | 'outline' | 'soft-primary' etc.
    pill = false,
    circle = false,
    size = "md",
    items = [], // Array de objetos { label, icon, onClick, danger, disabled, divider }
    customTrigger = null, // Caso queira passar um componente de gatilho arbitrário
    className = "",
    menuAlign = "end", // 'start' | 'end'
    children = null, // Suporta custom checklist/form layouts
    ...props
}) => {
    // Gatilho padrão do Dropdown
    const renderTrigger = () => {
        if (customTrigger) return customTrigger;

        if (circle) {
            return (
                <NeoButton
                    variant={variant}
                    circle
                    size={size}
                    icon={icon || "mdi-dots-vertical"}
                    type="button"
                />
            );
        }

        return (
            <NeoButton
                variant={variant}
                pill={pill}
                size={size}
                icon={icon}
                iconRight={label && iconRight ? iconRight : null}
                type="button"
            >
                {label}
            </NeoButton>
        );
    };

    return (
        <UncontrolledDropdown direction="down" className={`neo-dropdown-wrapper ${className}`} {...props}>
            <DropdownToggle tag="span" className="neo-dropdown-toggle-span">
                {renderTrigger()}
            </DropdownToggle>
            <DropdownMenu 
                flip={false}
                container="body"
                end={menuAlign === "end"} 
                className="neo-dropdown-menu"
            >
                {children ? children : items.map((item, idx) => {
                    if (item.divider) {
                        return <DropdownItem key={idx} divider className="neo-dropdown-divider" />;
                    }

                    return (
                        <DropdownItem
                            key={idx}
                            onClick={item.onClick}
                            disabled={item.disabled}
                            className={`neo-dropdown-item ${item.danger ? "neo-dropdown-item-danger" : ""}`}
                        >
                            {item.icon && (
                                <i className={`mdi ${item.icon} me-2 neo-dropdown-item-icon`} />
                            )}
                            <span className="flex-grow-1">{item.label}</span>
                        </DropdownItem>
                    );
                })}
            </DropdownMenu>
        </UncontrolledDropdown>
    );
};

NeoDropdown.propTypes = {
    label: PropTypes.string,
    icon: PropTypes.string,
    iconRight: PropTypes.string,
    variant: PropTypes.string,
    pill: PropTypes.bool,
    circle: PropTypes.bool,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            icon: PropTypes.string,
            onClick: PropTypes.func,
            danger: PropTypes.bool,
            disabled: PropTypes.bool,
            divider: PropTypes.bool
        })
    ),
    customTrigger: PropTypes.node,
    className: PropTypes.string,
    menuAlign: PropTypes.oneOf(["start", "end"]),
    children: PropTypes.node
};

export default NeoDropdown;
